import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { ApiEnvelope, ApiEnvelopeNoContent } from './types/api';
import { GetVehicleRouteEnvelope, GetVehicleRouteResponseVM } from './types/get-vehicle-route';
import {
  ForceFullDailyEcPreviewEnvelope,
  ForceFullDailyEcPreviewResponseVM,
} from './types/get-vehicle-force-full-daily-ec';
import { VehiclePolylineEnvelope, VehiclePolylineItemResponse } from './types/get-vehicle-polyline';
import { VehiclePolylineResponseEnvelope } from './types/get-vehicle-polylines';
import { ChargingPointResponse, GeospatialChargingPoint } from './types/get-charging-points';
import { UpdateSideMenuRequestVM } from './types/update-side-menu';
import { ListBevCandidatesResponse, ListBevCandidatesEnvelope } from './types/get-vehicles';
import { SimpleFleetRoutesEnvelope, SimpleFleetRoutes } from './types/get-simple-fleet';
import { WaypointsEnvelope } from './types/get-waypoints';
import { PowerNeedVehicle, PowerNeedEnvelope } from './types/get-power-need';
import { Waypoint } from './types/get-waypoint';
import {
  ImportedDataChassisDataResponse,
  ImportedDataChassisDataEnvelope,
} from './types/get-imported-data-chassis-data';
import { ChargingScheduleVehicle, ChargingScheduleEnvelope } from './types/get-charging-schedule';
import { UpdateChargingScheduleArguments } from './types/update-charging-schedule';
import { FleetSummaryResponse, ImportedDataFleetSummaryEnvelope } from './types/get-imported-data-fleet-summary';
import { GetVehicleArguments, GetVehicleResponseVM, GetVehicleEnvelope } from './types/get-vehicle';
import { SolutionHomeOverviewEnvelope, SolutionHomeOverviewResponseVM } from './types/get-solution-home-overview';
import {
  GetVehicleOverviewResponseVM,
  GetVehicleOverviewArguments,
  GetVehicleOverviewEnvelope,
} from './types/get-vehicle-overview';
import {
  GetVehicleSpecificationOptionsEnvelope,
  GetVehicleSpecificationOptionsArguments,
  GetVehicleSpecificationOptionsResponseVM,
} from './types/get-vehicle-specification-options';
import { GetStopsResponse, GetStopsRequestArguments, GetStopsResponseEnvelope } from './types/get-stops';
import { GetTripsResponse, GetTripsRequestArguments, GetTripsResponseEnvelope } from './types/get-trips';
import { UpdateVehicleSpecificationArguments } from './types/update-vehicle-specification';
import { SolutionMetadataEnvelope, SolutionMetadataVM } from './types/get-solutions';
import { SolutionVM, SolutionEnvelope } from './types/get-solution';
import { UserVM, GetUserEnvelope } from './types/get-user';
import { GetCustomersEnvelope, GetCustomersArguments } from './types/get-customers';
import {
  GetChassisByCustomerNameArguments,
  GetChassisByCustomerNameEnvelope,
} from './types/get-chassis-by-customer-name';
import {
  ValidateChassisNumbersResponse,
  ValidateChassisNumbersRequest,
  ValidateChassisNumbersEnvelope,
} from './types/validate-chassis';
import { AddChassisToSolutionArguments } from './types/add-chassis-to-solution';
import {
  GetVehicleSpecificationStatisticsResponseVM,
  GetVehicleSpecificationStatisticsArguments,
  GetVehicleSpecificationStatisticsEnvelope,
} from './types/get-vehicle-specification-statistics';
import { GetPtoDevicesEnvelope, GetPtoDevicesResponseVM } from './types/get-pto-devices';
import { GetPtoSummaryArguments, GetPtoSummaryEnvelope, GetPtoSummaryResponseVM } from './types/get-pto-summary';
import {
  GetBatterySummaryArguments,
  GetBatterySummaryEnvelope,
  GetBatterySummaryResponseVM,
} from './types/get-battery-summary';
import { UpdateVehicleOperationDaysPerYearArguments } from './types/update-vehicle-operation-days-per-year';
import { DeleteSolutionArguments } from './types/delete-solution';
import { UpdateSolutionNameArguments } from './types/update-solution-name';
import { UpdateVehiclePtoArguments } from '@/app/services/solution/types/update-vehicle-pto';
import getApiAccessToken from '@/features/Auth/utils/get-api-access-token';
import { SOLUTION_ENDPOINT } from '@/app/config';

const baseQuery = fetchBaseQuery({
  baseUrl: `${SOLUTION_ENDPOINT}/api`,
  prepareHeaders: async (headers) => {
    const accessToken = await getApiAccessToken();
    const xSessionId = sessionStorage.getItem('x_session_id');
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    if (xSessionId) {
      headers.set('x-session-id', xSessionId);
    }
    return headers;
  },
});

const baseQueryRetry = retry(baseQuery, {
  maxRetries: 0,
});

export const tagTypes = ['User', 'Vehicles', 'Solutions', 'Waypoints'] as const;

type SolutionTag = 'Solutions' | { type: 'Solutions'; id: number | string };
type VehicleTag = 'Vehicles' | { type: 'Vehicles'; id: number };
type WaypointTag = 'Waypoints' | { type: 'Waypoints'; id: number };
type VehicleWaypointTag = VehicleTag | WaypointTag;
type QueryParameterValue = string | number | readonly number[] | undefined;

const toQueryString = (parameters: Record<string, QueryParameterValue>): string => {
  const searchParameters = new URLSearchParams();

  for (const [key, value] of Object.entries(parameters)) {
    if (value !== undefined) {
      searchParameters.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }
  }

  return searchParameters.toString();
};

export const solutionApi = createApi({
  reducerPath: 'solutionApi',
  tagTypes,
  baseQuery: baseQueryRetry,
  endpoints: (build) => ({
    getSolutions: build.query<SolutionMetadataVM[], undefined>({
      query: () => ({ url: 'v1/solutions' }),
      providesTags: (solutions): SolutionTag[] => {
        const tags: SolutionTag[] = ['Solutions'];

        if (solutions) {
          for (const solution of solutions) {
            tags.push({ type: 'Solutions' as const, id: solution.id });
          }
        }

        return tags;
      },
      transformResponse: (response: SolutionMetadataEnvelope) => response.content,
    }),
    getSolution: build.query<SolutionVM, number | string>({
      query: (id) => ({ url: `v1/solutions/${id}` }),
      providesTags: (_, __, id) => [{ type: 'Solutions', id }],
      transformResponse: (response: SolutionEnvelope) => response.content,
    }),
    addSolution: build.mutation<SolutionEnvelope, Partial<SolutionVM>>({
      query: (body) => ({ url: 'v1/solutions', method: 'POST', body }),
      invalidatesTags: ['Solutions'],
    }),
    deleteSolution: build.mutation<ApiEnvelopeNoContent, DeleteSolutionArguments>({
      query: ({ solutionId }) => ({
        url: `v1/solutions/${solutionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { solutionId }) => ['Solutions', { type: 'Solutions', id: solutionId }],
    }),
    updateSolution: build.mutation<null, Partial<SolutionVM>>({
      query: ({ id, ...body }) => ({
        url: `v1/solutions/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Solutions', id }],
    }),
    updateSolutionName: build.mutation<ApiEnvelopeNoContent, UpdateSolutionNameArguments>({
      query: ({ solutionId, name }) => ({
        url: `v1/solutions/${solutionId}/name`,
        method: 'PUT',
        body: { name },
      }),
      invalidatesTags: (_, __, { solutionId }) => ['Solutions', { type: 'Solutions', id: solutionId }],
    }),
    getUser: build.query<UserVM, undefined>({
      query: () => ({
        url: 'v1/current-user/self',
      }),
      providesTags: () => ['User'],
      transformResponse: (response: GetUserEnvelope) => response.content,
    }),
    updateTos: build.mutation<null, undefined>({
      query: () => ({
        url: 'v1/current-user/terms-of-service',
        method: 'PUT',
      }),
      invalidatesTags: () => ['User'],
    }),
    updateSideMenu: build.mutation<null, UpdateSideMenuRequestVM>({
      query: ({ isSideMenuOpen }) => ({
        url: 'v1/current-user/side-menu',
        method: 'PUT',
        params: { isSideMenuOpen },
      }),
      invalidatesTags: () => ['User'],
    }),
    getCustomers: build.query<string[], GetCustomersArguments>({
      query: ({ searchTerm }: { searchTerm: string }) => ({
        url: `v1/customers?${toQueryString({ searchTerm })}`,
      }),
      transformResponse: (response: GetCustomersEnvelope) => response.content.customerNames,
    }),
    getChassisByCustomerName: build.query<number[], GetChassisByCustomerNameArguments>({
      query: ({ customerName }: { customerName: string }) => ({
        url: `v1/customers/chassis-numbers?${toQueryString({ customerName })}`,
      }),
      transformResponse: (response: GetChassisByCustomerNameEnvelope) => response.content.chassisNumbers,
    }),
    validateChassis: build.mutation<ValidateChassisNumbersResponse, { body: ValidateChassisNumbersRequest }>({
      query: ({ body }) => ({
        url: `v1/chassis-numbers/validate`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ValidateChassisNumbersEnvelope) => response.content,
    }),
    getSolutionHomeOverview: build.query<SolutionHomeOverviewResponseVM, string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/overview` }),
      transformResponse: (response: SolutionHomeOverviewEnvelope) => response.content,
    }),
    getImportedDataFleetSummary: build.query<FleetSummaryResponse, string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/imported-data/fleet-summary` }),
      transformResponse: (response: ImportedDataFleetSummaryEnvelope) => response.content,
    }),
    getImportedDataChassisData: build.query<ImportedDataChassisDataResponse, string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/imported-data/chassis-data` }),
      transformResponse: (response: ImportedDataChassisDataEnvelope) => response.content,
    }),
    getVehicle: build.query<GetVehicleResponseVM, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }: GetVehicleArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}`,
      }),
      providesTags: (_, __, arguments_: GetVehicleArguments) => [
        { type: 'Vehicles', id: Number(arguments_.vehicleId) },
      ],
      transformResponse: (response: GetVehicleEnvelope) => response.content,
    }),
    getVehicleRoute: build.query<GetVehicleRouteResponseVM, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v3/solutions/${solutionId}/vehicles/${vehicleId}/route`,
      }),
      transformResponse: (response: GetVehicleRouteEnvelope) => response.content,
    }),
    getVehicleForceFullDailyEc: build.query<ForceFullDailyEcPreviewResponseVM, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/force-full-daily-ec`,
        method: 'POST',
      }),
      transformResponse: (response: ForceFullDailyEcPreviewEnvelope) => response.content,
    }),
    getVehiclePolylines: build.query<VehiclePolylineItemResponse[], string>({
      query: (solutionId) => ({
        url: `v1/solutions/${solutionId}/vehicles/polylines`,
      }),
      transformResponse: (response: VehiclePolylineResponseEnvelope) => response.content.items,
    }),
    getVehiclePolyline: build.query<VehiclePolylineItemResponse, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/polyline`,
      }),
      transformResponse: (response: VehiclePolylineEnvelope) => response.content,
    }),
    getVehicleOverview: build.query<GetVehicleOverviewResponseVM, GetVehicleOverviewArguments>({
      query: ({ solutionId, vehicleId, day, temperature }: GetVehicleOverviewArguments) => ({
        url: `v3/solutions/${solutionId}/vehicles/${vehicleId}/overview?day=${day}&temperature=${temperature}`,
      }),
      providesTags: (_, __, arguments_: GetVehicleOverviewArguments) => [
        { type: 'Vehicles', id: Number(arguments_.vehicleId) },
      ],
      transformResponse: (response: GetVehicleOverviewEnvelope) => response.content,
    }),
    getVehicleSpecificationStatistics: build.query<
      GetVehicleSpecificationStatisticsResponseVM,
      GetVehicleSpecificationStatisticsArguments
    >({
      query: ({ solutionId, vehicleId }) => ({
        url: `v3/solutions/${solutionId}/vehicles/${vehicleId}/specification-statistics`,
      }),
      providesTags: (_, __, { vehicleId }) => [{ type: 'Vehicles', id: vehicleId }],
      transformResponse: (response: GetVehicleSpecificationStatisticsEnvelope) => response.content,
    }),
    getVehicleSpecificationOptions: build.query<
      GetVehicleSpecificationOptionsResponseVM,
      GetVehicleSpecificationOptionsArguments
    >({
      query: ({ solutionId, ...parameters }: GetVehicleSpecificationOptionsArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/specification-options?${toQueryString(parameters)}`,
      }),
      providesTags: (_, __, arguments_: { vehicleIds?: number[] }): VehicleTag[] => {
        const { vehicleIds } = arguments_;

        if (vehicleIds && vehicleIds.length > 0) {
          const tags: VehicleTag[] = ['Vehicles'];
          for (const id of vehicleIds) {
            tags.push({ type: 'Vehicles', id });
          }
          return tags;
        }
        return ['Vehicles'];
      },
      transformResponse: (response: GetVehicleSpecificationOptionsEnvelope) => response.content,
    }),
    updateVehiclePto: build.mutation<null, UpdateVehiclePtoArguments>({
      query: ({ solutionId, vehicleIds, pto }: UpdateVehiclePtoArguments) => ({
        url: `v3/solutions/${solutionId}/vehicles/batch/pto`,
        method: 'PUT',
        body: { pto, vehicleIds },
      }),
      invalidatesTags: (_, __, arguments_: UpdateVehiclePtoArguments): VehicleTag[] => {
        const { vehicleIds } = arguments_;

        const tags: VehicleTag[] = [];
        for (const vehicleId of vehicleIds) {
          tags.push({ type: 'Vehicles', id: vehicleId });
        }
        return tags;
      },
    }),
    updateVehicleSpecification: build.mutation<null, UpdateVehicleSpecificationArguments>({
      query: ({ solutionId, updateVehicleSpecificationBody }: UpdateVehicleSpecificationArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/specifications`,
        method: 'PUT',
        body: updateVehicleSpecificationBody,
      }),
      invalidatesTags: (_, __, arguments_: UpdateVehicleSpecificationArguments): VehicleTag[] => {
        const { updateVehicleSpecificationBody } = arguments_;
        const tags: VehicleTag[] = [];
        for (const vehicleId of updateVehicleSpecificationBody.vehicleIds) {
          tags.push({ type: 'Vehicles', id: vehicleId });
        }
        return tags;
      },
    }),
    updateVehicleOperationDaysPerYear: build.mutation<null, UpdateVehicleOperationDaysPerYearArguments>({
      query: ({ solutionId, vehicleIds, operatingDaysPerYear }) => ({
        url: `v3/solutions/${solutionId}/vehicles/batch/operation-days-per-year`,
        method: 'PUT',
        body: { operatingDaysPerYear, vehicleIds },
      }),
      invalidatesTags: (_, __, { vehicleIds }: UpdateVehicleOperationDaysPerYearArguments): VehicleTag[] =>
        vehicleIds.map((vehicleId) => ({ type: 'Vehicles', id: vehicleId })),
    }),
    getVehicles: build.query<ListBevCandidatesResponse[], string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/vehicles` }),
      providesTags: (vehicles): VehicleTag[] => {
        const tags: VehicleTag[] = ['Vehicles'];

        if (vehicles) {
          for (const vehicle of vehicles) {
            tags.push({ type: 'Vehicles' as const, id: vehicle.id });
          }
        }

        return tags;
      },
      transformResponse: (response: ListBevCandidatesEnvelope) => response.content.items,
    }),
    addChassisToSolution: build.mutation<ApiEnvelopeNoContent, AddChassisToSolutionArguments>({
      query: ({ solutionId, ...body }) => ({
        url: `v1/solutions/${solutionId}/vehicles/chassis`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { solutionId }) => [{ type: 'Solutions', id: solutionId }, 'Vehicles'],
    }),
    getWaypoints: build.query<Waypoint[], string>({
      query: (solutionId) => ({ url: `v3/solutions/${solutionId}/waypoints` }),
      providesTags: (waypoints): WaypointTag[] => {
        const tags: WaypointTag[] = ['Waypoints'];

        if (waypoints) {
          for (const waypoint of waypoints) {
            tags.push({ type: 'Waypoints' as const, id: waypoint.id });
          }
        }

        return tags;
      },
      transformResponse: (response: WaypointsEnvelope) => response.content.items,
    }),
    getPowerNeed: build.query<PowerNeedVehicle[], string>({
      query: (solutionId) => ({ url: `v3/solutions/${solutionId}/power-need` }),
      providesTags: (powerNeedVehicles): VehicleWaypointTag[] => {
        const tags: VehicleWaypointTag[] = ['Vehicles', 'Waypoints'];

        if (powerNeedVehicles) {
          for (const powerNeedVehicle of powerNeedVehicles) {
            tags.push(
              { type: 'Vehicles' as const, id: powerNeedVehicle.vehicleId },
              { type: 'Waypoints' as const, id: powerNeedVehicle.waypointId },
            );
          }
        }

        return tags;
      },
      transformResponse: (response: PowerNeedEnvelope) => response.content.items,
    }),
    getChargingSchedule: build.query<ChargingScheduleVehicle[], string>({
      query: (solutionId) => ({ url: `v3/solutions/${solutionId}/charging-schedule` }),
      providesTags: (chargingScheduleVehicles): VehicleWaypointTag[] => {
        const tags: VehicleWaypointTag[] = ['Vehicles', 'Waypoints'];

        if (chargingScheduleVehicles) {
          for (const chargingScheduleVehicle of chargingScheduleVehicles) {
            tags.push(
              { type: 'Vehicles' as const, id: chargingScheduleVehicle.vehicleId },
              { type: 'Waypoints' as const, id: chargingScheduleVehicle.waypointId },
            );
          }
        }

        return tags;
      },
      transformResponse: (response: ChargingScheduleEnvelope) => response.content.items,
    }),
    updateChargingSchedule: build.mutation<ApiEnvelopeNoContent, UpdateChargingScheduleArguments>({
      query: ({ solutionId, chargingScheduleVehicle }: UpdateChargingScheduleArguments) => {
        return {
          url: `v2/solutions/${solutionId}/charging-schedule`,
          method: 'PUT',
          body: chargingScheduleVehicle,
        };
      },
      invalidatesTags: (_, __, arguments_: UpdateChargingScheduleArguments) => {
        const { chargingScheduleVehicle } = arguments_;

        return [
          { type: 'Waypoints' as const, id: chargingScheduleVehicle.waypointId },
          { type: 'Vehicles' as const, id: chargingScheduleVehicle.vehicleId },
        ];
      },
    }),
    getFleetRoutes: build.query<SimpleFleetRoutes, string>({
      query: (solutionId) => ({ url: `v3/solutions/${solutionId}/fleet-routes` }),
      transformResponse: (response: SimpleFleetRoutesEnvelope) => response.content,
    }),
    getStops: build.mutation<GetStopsResponse, GetStopsRequestArguments>({
      query: ({ solutionId, ...body }: GetStopsRequestArguments) => ({
        url: `v1/solutions/${solutionId}/stops`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetStopsResponseEnvelope) => response.content,
    }),
    getTrips: build.mutation<GetTripsResponse, GetTripsRequestArguments>({
      query: ({ solutionId, ...body }: GetTripsRequestArguments) => ({
        url: `v1/solutions/${solutionId}/trips`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetTripsResponseEnvelope) => response.content,
    }),
    getChargingPoints: build.mutation<GeospatialChargingPoint[], undefined>({
      query: () => ({
        url: `v1/fact/charging-points`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: ApiEnvelope<ChargingPointResponse>) => response.content.chargingPoints,
    }),
    getPtoDevices: build.query<GetPtoDevicesResponseVM, undefined>({
      query: () => ({
        url: `v3/pto-devices`,
      }),
      transformResponse: (response: GetPtoDevicesEnvelope) => response.content,
    }),
    getPtoSummary: build.query<GetPtoSummaryResponseVM, GetPtoSummaryArguments>({
      query: ({ solutionId, vehicleId, ...parameters }: GetPtoSummaryArguments) => ({
        url: `v3/solutions/${solutionId}/vehicles/${vehicleId}/pto-summary?${toQueryString(parameters)}`,
      }),
      transformResponse: (response: GetPtoSummaryEnvelope) => response.content,
    }),
    getBatterySummary: build.query<GetBatterySummaryResponseVM, GetBatterySummaryArguments>({
      query: ({ solutionId, vehicleId, ...parameters }: GetBatterySummaryArguments) => ({
        url: `v3/solutions/${solutionId}/vehicles/${vehicleId}/battery-summary?${toQueryString(parameters)}`,
      }),
      transformResponse: (response: GetBatterySummaryEnvelope) => response.content,
    }),
  }),
});

export const {
  useAddChassisToSolutionMutation,
  useAddSolutionMutation,
  useDeleteSolutionMutation,
  useGetChargingPointsMutation,
  useGetChargingScheduleQuery,
  useGetFleetRoutesQuery,
  useGetImportedDataChassisDataQuery,
  useGetImportedDataFleetSummaryQuery,
  useGetPtoDevicesQuery,
  useGetPowerNeedQuery,
  useGetSolutionHomeOverviewQuery,
  useGetSolutionQuery,
  useGetSolutionsQuery,
  useGetStopsMutation,
  useGetTripsMutation,
  useGetUserQuery,
  useGetVehicleForceFullDailyEcQuery,
  useGetVehicleOverviewQuery,
  useGetVehiclePolylineQuery,
  useGetVehiclePolylinesQuery,
  useGetVehicleQuery,
  useGetVehicleRouteQuery,
  useGetVehicleSpecificationStatisticsQuery,
  useGetVehiclesQuery,
  useGetWaypointsQuery,
  useLazyGetPtoSummaryQuery,
  useLazyGetChassisByCustomerNameQuery,
  useLazyGetCustomersQuery,
  useLazyGetVehicleSpecificationOptionsQuery,
  useLazyGetBatterySummaryQuery,
  useUpdateChargingScheduleMutation,
  useUpdateSideMenuMutation,
  useUpdateSolutionNameMutation,
  useUpdateSolutionMutation,
  useUpdateTosMutation,
  useUpdateVehicleSpecificationMutation,
  useUpdateVehiclePtoMutation,
  useUpdateVehicleOperationDaysPerYearMutation,
  useValidateChassisMutation,
} = solutionApi;
